import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getSupabaseUrl, getSupabaseAnonKey } from '../lib/environments';

type Status = 'loading' | 'ready' | 'submitting' | 'sent' | 'error';

interface RecentMessage {
  id: string;
  sender_name: string;
  content_text: string;
  created_at: string;
}

interface ReplyContext {
  conversation_title: string;
  sender_name: string;
  recent: RecentMessage[];
}

export const EmailReply: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [status, setStatus] = useState<Status>('loading');
  const [error, setError] = useState('');
  const [context, setContext] = useState<ReplyContext | null>(null);
  const [body, setBody] = useState('');

  const apiBase = `${getSupabaseUrl()}/functions/v1/email-reply-submit`;

  useEffect(() => {
    if (!token) { setError('Missing reply token.'); setStatus('error'); return; }

    fetch(`${apiBase}?t=${encodeURIComponent(token)}`, {
      headers: { apikey: getSupabaseAnonKey() },
    })
      .then((res) => res.json())
      .then((data) => {
        if (!data.success) {
          setError(data.error || 'Could not load this reply link.');
          setStatus('error');
          return;
        }
        setContext({
          conversation_title: data.conversation_title,
          sender_name: data.sender_name,
          recent: data.recent || [],
        });
        setStatus('ready');
      })
      .catch(() => { setError('Network error. Please try again.'); setStatus('error'); });
  }, [token]);

  const submit = async () => {
    if (!body.trim() || !token) return;
    setStatus('submitting');
    setError('');
    try {
      const res = await fetch(apiBase, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', apikey: getSupabaseAnonKey() },
        body: JSON.stringify({ t: token, content_text: body.trim() }),
      });
      const data = await res.json();
      if (!data.success) { setError(data.error || 'Failed to send.'); setStatus('ready'); return; }
      setStatus('sent');
    } catch {
      setError('Network error. Please try again.');
      setStatus('ready');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4">
      <div className="max-w-xl mx-auto bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
        <div className="bg-gradient-to-br from-[#1e3c72] to-[#2a5298] text-white px-6 py-5">
          <h1 className="text-2xl font-semibold m-0">Kizu</h1>
          {context && status !== 'sent' && (
            <p className="text-sm opacity-90 mt-1 m-0">Replying to {context.conversation_title}</p>
          )}
        </div>

        <div className="px-6 py-6 text-gray-800 dark:text-gray-200">
          {status === 'loading' && (
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
          )}

          {status === 'error' && (
            <>
              <h2 className="text-lg font-semibold m-0 mb-2">Can't open this link</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">{error}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                Reply links expire after 30 days. Open the Kizu app to reply directly.
              </p>
            </>
          )}

          {(status === 'ready' || status === 'submitting') && context && (
            <>
              {context.recent.length > 0 && (
                <div className="mb-4 space-y-2 max-h-56 overflow-y-auto">
                  {context.recent.map((m) => (
                    <div key={m.id} className="text-sm">
                      <div className="font-medium text-gray-700 dark:text-gray-300">{m.sender_name}</div>
                      <div className="text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{m.content_text}</div>
                    </div>
                  ))}
                </div>
              )}

              <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                You're replying as <strong>{context.sender_name}</strong>
              </p>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Type your reply…"
                rows={5}
                maxLength={4000}
                disabled={status === 'submitting'}
                className="w-full px-3 py-2 text-base border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
              />

              {error && <p className="text-sm text-red-600 dark:text-red-400 mt-2">{error}</p>}

              <div className="flex justify-end mt-4">
                <button
                  onClick={submit}
                  disabled={status === 'submitting' || !body.trim()}
                  className="px-5 py-2 bg-indigo-600 text-white rounded-md font-semibold hover:bg-indigo-700 disabled:opacity-50"
                >
                  {status === 'submitting' ? 'Sending…' : 'Send'}
                </button>
              </div>
            </>
          )}

          {status === 'sent' && (
            <>
              <h2 className="text-lg font-semibold m-0 mb-2">Sent ✓</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Your reply was posted to {context?.conversation_title || 'the conversation'}.
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                You can close this tab. Open the Kizu app to keep chatting.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
